import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { t } from "i18next";
import { throttle } from "lodash";

import { MessageSeverity } from "primereact/api";

import { SignalREventNames } from "../../../../../@api";

import { TokenRefreshManager } from "../../../../../auth/TokenRefreshManager";
import { apiConfig } from "../../../../../environmentConfiguration";
import { translationKeys } from "../../../../models/app-translation-keys";
import { store } from "../../../../stores/StoreContext";
import { IAsyncConnection } from "../async.connection.interface";
import { SignalRMessageFilter } from "./SignalRMessageFilter";

const activeHubConnectionStates = [
  HubConnectionState.Connected,
  HubConnectionState.Connecting,
  HubConnectionState.Reconnecting,
];

const showAccessTokenErrorToast = throttle(
  () =>
    store.toastStore.addToast({
      severity: MessageSeverity.ERROR,
      summary: { message: t(translationKeys.globalErrors.authorization) },
      detail: { message: "" },
      sticky: true,
    }),
  1000,
  { leading: true, trailing: false },
);

export class SignalRConnection implements IAsyncConnection {
  private readonly messageFilters: SignalRMessageFilter[];
  private readonly connection: HubConnection;

  constructor(
    private readonly hubName: string,
    ...messageFilters: SignalRMessageFilter[]
  ) {
    this.messageFilters = messageFilters;

    this.connection = new HubConnectionBuilder()
      .withUrl(`${apiConfig.baseUrl}/${hubName}`, {
        accessTokenFactory: () =>
          TokenRefreshManager.getInstance()
            .refreshAccessToken()
            .then(token => {
              const accessToken = token || store.userStore.userAccessToken;
              if (!accessToken) {
                showAccessTokenErrorToast();
                throw new Error("Access token is not available");
              }

              return accessToken;
            }),
      })
      .configureLogging(LogLevel.Error)
      .withAutomaticReconnect()
      .build();

    this.connection.serverTimeoutInMilliseconds = 300000; // 300 Sekunden = 5 Minuten
    this.connection.keepAliveIntervalInMilliseconds = 300000; // 300 Sekunden = 5 Minuten
  }

  /**
   * Response is ignored when messageFilters are defined but none matches
   * @param methodName
   * @param callbackFunction
   */
  public on<T>(methodName: SignalREventNames, callbackFunction: (response: T) => void): void {
    this.connection.on(methodName, (response: T) => {
      if (
        !response ||
        this.messageFilters.length == 0 ||
        this.messageFilters.some(messageFilter => messageFilter(response))
      ) {
        callbackFunction(response);
      }
    });
  }

  public invoke<TResult>(methodName: string, ...args: unknown[]): Promise<TResult> {
    return this.connection.invoke<TResult>(methodName, ...args);
  }

  public onClose(callback: (error?: Error) => void): void {
    this.connection.onclose(callback);
  }

  public get isActive(): boolean {
    return !!this.connection && activeHubConnectionStates.includes(this.connection.state);
  }

  public get state(): HubConnectionState {
    return this.connection.state;
  }

  public async start(): Promise<void> {
    try {
      await this.connection.start();
      console.debug(`Sucessfully connected to SignalR hub with name '${this.hubName}'.`);
    } catch (error: any) {
      console.error(`Failed to connect to SignalR hub with name '${this.hubName}':`, error);
      this.stop();
    }
  }

  public async stop(): Promise<void> {
    if (
      !this.connection ||
      this.connection.state === HubConnectionState.Disconnected ||
      this.connection.state === HubConnectionState.Disconnecting
    ) {
      return;
    }

    await this.connection.stop();
    console.debug(`SignalR connection to hub with name ${this.hubName} has been stopped.`);
  }
}
